<template>
    <nav :class="[$style['mds-tabs'], classObject]" :data-mds-version="version">
        <ul ref="container" role="tablist" :class="$style['mds-tabs__inner']">
            <slot />
        </ul>
    </nav>
</template>

<script>
    import packageInfo from '../package.json';
    import { computed } from 'vue';

    const { name, version } = packageInfo;

    export default {
        name: 'MdsTabs',
        props: {
            alignCenter: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'Sets the alignment of the tabs items to center.',
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            responsive: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, turns on the overflow responsiveness of tabs. Implementing teams also need to call the `responsiveTabs()` method in order to trigger the responsive behavior.',
                },
            },
            siteNavigation: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'Sets the site navigation variation of tabs',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`.',
                    description: 'Sets the size of the containted tabs items.',
                },
            },
        },
        provide() {
            return {
                onDark: computed(() => this.onDark),
                size: computed(() => this.size),
            };
        },
        computed: {
            classObject() {
                const obj = {};
                obj[this.$style['mds-tabs--site-navigation']] = this.siteNavigation;
                obj[this.$style[`mds-tabs--large`]] = this.size === 'large';
                obj[this.$style['mds-tabs--centered']] = this.alignCenter;
                obj[this.$style['mds-component--on-dark']] = this.onDark;
                obj[this.$style['mds-tabs--responsive']] = this.responsive;
                obj[this.$style['mds-tabs--responsive-left']] = this.responsive && this.overflowLeft;
                obj[this.$style['mds-tabs--responsive-right']] = this.responsive && this.overflowRight;

                return obj;
            },
        },
        watch: {
            responsive(newVal) {
                if (newVal) {
                    this.responsiveTabs();
                }
            },
        },
        data() {
            return {
                overflowRight: false,
                overflowLeft: false,
                version: `${name}-${version}`,
            };
        },
        methods: {
            responsiveTabs() {
                const containerScrollWidth = this.containerEl.scrollWidth;
                const containerOffsetWidth = this.containerEl.offsetWidth;
                const containerScrollLeft = Math.round(this.containerEl.scrollLeft);
                const containerScrollLeftMax = this.containerEl.scrollWidth - this.containerEl.clientWidth;
                this.overflowRight = containerScrollWidth > containerOffsetWidth;
                this.overflowLeft = containerScrollLeft > 0;
                this.overflowRight = containerScrollLeft !== containerScrollLeftMax;
                this.containerEl.addEventListener('scroll', this.responsiveTabs);
            },
        },
        mounted() {
            this.containerEl = this.$refs['container'];
            if (this.responsive) {
                this.responsiveTabs();
            }
        },
    };
</script>

<style lang="scss" module>
    @use './tabs.scss';
</style>
